import axios from 'axios';

const api = axios.create({
  baseURL: '/api',
  headers: { 'Content-Type': 'application/json' },
  timeout: 30000,
});

// 토큰 갱신 중복 방지
let isRefreshing = false;
let refreshSubscribers = [];

function onTokenRefreshed(newToken) {
  refreshSubscribers.forEach((cb) => cb(newToken));
  refreshSubscribers = [];
}

function addRefreshSubscriber(cb) {
  refreshSubscribers.push(cb);
}

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  const vid = localStorage.getItem('visitor_id');
  if (vid) config.headers['X-Visitor-Id'] = vid;
  return config;
}, (error) => Promise.reject(error));

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && originalRequest?.skipAuthRedirect) {
      return Promise.reject(error);
    }

    // 401 에러이고 refresh 요청 자체가 아닌 경우에만 토큰 갱신 시도
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      originalRequest?.url !== '/auth/refresh' &&
      originalRequest?.url !== '/auth/login'
    ) {
      originalRequest._retry = true;

      if (isRefreshing) {
        // 이미 갱신 중이면 갱신 완료 후 재시도
        return new Promise((resolve) => {
          addRefreshSubscriber((newToken) => {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            resolve(api(originalRequest));
          });
        });
      }

      isRefreshing = true;

      try {
        const token = localStorage.getItem('token');
        if (!token) throw new Error('No token');

        const res = await axios.post('/api/auth/refresh', {}, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 10000,
        });

        if (res.data.success && res.data.token) {
          const newToken = res.data.token;
          localStorage.setItem('token', newToken);
          onTokenRefreshed(newToken);
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return api(originalRequest);
        }
        throw new Error('Refresh failed');
      } catch {
        // 토큰 갱신 실패 시 로그아웃
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        refreshSubscribers = [];
        if (window.location.pathname !== '/login') window.location.href = '/login';
        return Promise.reject(error);
      } finally {
        isRefreshing = false;
      }
    }

    // refresh 자체가 실패하거나 login 401인 경우
    if (error.response?.status === 401 && originalRequest?.url === '/auth/refresh' && !originalRequest?.skipAuthRedirect) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      if (window.location.pathname !== '/login') window.location.href = '/login';
    }

    return Promise.reject(error);
  }
);

// ═══ 사주+농사 융합 API ═══
const FARM_TIMEOUT = { timeout: 120000 }; // Gemini AI 호출 포함 → 120초
export const sajuFarmAPI = {
  getOhengPlant: (payload) => api.post('/saju-farm/oheng-plant', payload, FARM_TIMEOUT),
  getSajuYakson: (payload) => api.post('/saju-farm/saju-yakson', payload, FARM_TIMEOUT),
  getJeolgiFortune: (payload) => api.post('/saju-farm/jeolgi-fortune', payload, FARM_TIMEOUT),
  getHealingFarm: (payload) => api.post('/saju-farm/healing-farm', payload, FARM_TIMEOUT),
  getComprehensive: (payload) => api.post('/saju-farm/comprehensive', payload, FARM_TIMEOUT),
  getPungsuWeather: (payload) => api.post('/saju-farm/pungsu-weather', payload, FARM_TIMEOUT),
  getDataSources: () => api.get('/saju-farm/data-sources'),
  searchAnnals: (payload) => api.post('/saju-farm/annals-search', payload, FARM_TIMEOUT),
  searchGeography: (payload) => api.post('/saju-farm/geography-search', payload, FARM_TIMEOUT),
};

export const reviewsAPI = {
  getLive: (params = {}) => api.get('/reviews/live', { params }).then((res) => res.data),
  list: (params = {}) => api.get('/reviews/list', { params }).then((res) => res.data),
  createLive: (payload) => api.post('/reviews/live', payload).then((res) => res.data),
};

// ═══ 유명인 사주 공개 카드 (화면설계서 DDM0130 / DDM0200) ═══
export const famousSajuAPI = {
  getPublic: () => api.get('/famous-saju').then((res) => res.data),
  list: (params = {}) => api.get('/famous-saju/list', { params }).then((res) => res.data),
  createFromAnalysis: (payload) =>
    api.post('/famous-saju/admin/from-analysis', payload).then((res) => res.data),
};

// ═══ 사주풀이 이메일 전송 (화면설계서 DDM0120) ═══
export const shareAPI = {
  emailSajuReading: (payload) => api.post('/share/saju-email', payload).then((res) => res.data),
};

export default api;
